import type { ID } from '@/shared/types'
import type { IconName } from '@/shared/ui/Icon'
import type { ChatThreadData, ComposerLimits } from './types'
import { DRAFT_CONVERSATION_ID } from '@/features/conversations/mock'

/** Mock thread keyed by conversation id. In a real implementation this */
/** would be replaced by a `useConversationMessages(id)` hook hitting the */
/** backend. The draft id maps to an empty thread for the "Nova consulta" */
/** flow. */
export const mockThreads: Record<ID, ChatThreadData> = {
  [DRAFT_CONVERSATION_ID]: {
    conversationId: DRAFT_CONVERSATION_ID,
    title: 'Nova consulta',
    messages: [],
  },

  'conv-pediatria-2025': {
    conversationId: 'conv-pediatria-2025',
    title: 'Protocolos pediátricos — comparativo 2024/2025',
    messages: [
      {
        id: 'm-u-1',
        role: 'user',
        createdAt: '2026-05-25T14:31:40Z',
        text: 'Resuma as principais diferenças entre os protocolos de internação pediátrica documentados em 2024 vs. 2025. Cite os trechos exatos que sustentam cada ponto.',
      },
      {
        id: 'm-a-1',
        role: 'assistant',
        createdAt: '2026-05-25T14:32:00Z',
        responseSeconds: 1.2,
        sourceIds: ['src-uti-2025', 'src-internacao-2024', 'src-consentimento-2025', 'src-sbp-2025'],
        blocks: [
          {
            kind: 'paragraph',
            text: 'Os documentos indexados mostram **três deslocamentos centrais** entre o protocolo de 2024 e o de 2025. Resumindo a partir dos trechos mais relevantes recuperados do índice:',
          },
          {
            kind: 'bullets',
            items: [
              '**Critérios de admissão na UTI Pediátrica** — o limiar de saturação foi ajustado de **SpO₂ < 92%** (2024) para **SpO₂ < 94%** (2025), antecipando internações em quadros respiratórios leves a moderados.',
              '**Reavaliação medicamentosa** — o intervalo padrão passou de **24h** para **12h** nas primeiras 72 horas, com obrigatoriedade de registro estruturado por dose.',
              '**Consentimento informado** — o protocolo de 2025 introduz consentimento **específico por procedimento**, substituindo o termo único de internação utilizado em 2024.',
            ],
          },
          {
            kind: 'callout',
            text: 'Observação: a comparação foi feita a partir de 4 documentos no tenant **clinica-atenas**, escopo `protocolo/pediatria`. Para detalhar qualquer ponto, peça por exemplo: *"mostre os trechos sobre reavaliação medicamentosa"*.',
          },
          {
            kind: 'paragraph',
            text: 'Posso seguir aprofundando qualquer um desses pontos, gerar uma tabela comparativa lado a lado, ou exportar este resumo como relatório PDF.',
          },
        ],
      },
    ],
  },

  'conv-auditoria-mar': {
    conversationId: 'conv-auditoria-mar',
    title: 'Auditoria de prontuários · março',
    messages: [
      {
        id: 'm-u-2',
        role: 'user',
        createdAt: '2026-05-24T10:12:00Z',
        text: 'Liste os prontuários de março com inconsistências entre prescrição e administração.',
      },
      {
        id: 'm-a-2',
        role: 'assistant',
        createdAt: '2026-05-24T10:14:00Z',
        responseSeconds: 0.9,
        sourceIds: ['src-internacao-2024'],
        blocks: [
          {
            kind: 'paragraph',
            text: 'Identifiquei **3 prontuários** em março com divergência entre prescrição e administração registradas. Selecione um para abrir os trechos exatos.',
          },
        ],
      },
    ],
  },
}

export function getThread(conversationId: ID): ChatThreadData {
  return (
    mockThreads[conversationId] ?? {
      conversationId,
      title: 'Conversa',
      messages: [],
    }
  )
}

export const mockComposerLimits: ComposerLimits = {
  maxTokens: 3000,
  currentTokens: 680,
}

export const composerDisclaimer =
  'Rager pode cometer erros · sempre verifique citações e fontes recuperadas.'

/** Copy for the prompt-first home screen (no conversation selected). */
export const homeScreenCopy = {
  eyebrow: 'Workspace · Consulta',
  titleLead: 'O que você quer',
  titleAccent: 'consultar',
  titleTail: ' hoje?',
  subtitle:
    'Pergunte algo sobre seus documentos indexados. Rager responde com base apenas no que está no seu índice, citando trechos verificáveis.',
  placeholder: 'Pergunte algo sobre seus documentos…',
} as const

export interface HomeQuickAction {
  key: string
  label: string
  icon: IconName
  /** Prefilled text pushed into the composer when clicked. */
  prompt: string
}

export const homeQuickActions: HomeQuickAction[] = [
  {
    key: 'analisar',
    label: 'Analisar documentos',
    icon: 'doc',
    prompt: 'Analise os documentos indexados e destaque os pontos mais relevantes.',
  },
  {
    key: 'comparar',
    label: 'Comparar fontes',
    icon: 'grid',
    prompt: 'Compare as duas fontes mais recentes e mostre as principais divergências.',
  },
  {
    key: 'protocolos',
    label: 'Buscar protocolos',
    icon: 'search',
    prompt: 'Liste os protocolos disponíveis e onde aparecem nos documentos.',
  },
  {
    key: 'resumir',
    label: 'Resumir caso',
    icon: 'sparkles',
    prompt: 'Resuma o caso mais relevante recuperado do índice, com citações.',
  },
]
